import { channels } from '@/_shared/constants/channels.constants';
import { country } from '@/_shared/constants/country.constants';
import {
  ModuleBPeriod,
  ShopeeForecastMetric,
  ShopeeForecastValue,
} from '../_api/models/module-b.model';
import {
  DataPoint,
  TradewindsBaseData,
} from '../models/tradewinds/tradewinds.model';

// UI util
export const parseCellValue = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};
export const roundParseCellValue = (value: number) => {
  const hasDecimal = value % 1 !== 0;

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: hasDecimal ? 2 : 0,
  }).format(value);
};

export const cellColor = (v: number) => {
  const rounded = Number(v.toFixed(2));
  if (rounded < 0) return 'bg-red-100';
  if (rounded > 0) return 'bg-teal-100';
  return 'bg-gray-50';
};

export const textColor = (v: number) => {
  const rounded = Number(v.toFixed(2));
  if (rounded === 0) return 'text-black';
  if (rounded > 0) return 'text-teal-800';
  return 'text-red-800';
};

export const parseValueWithUnit = (value: number, unit: string) => {
  return `${value < 0 ? '-' : ''}${unit.includes('USD') ? '$' : ''}${roundParseCellValue(Math.abs(value))}${unit.includes('%') ? '' : ' '}${unit.replace('USD', '').trim()}`;
};

// Module C (Seller Panel) has no unit column in the DB — every metric name
// gets classified here. Most metrics are stored as a 0-1 fraction (rates,
// growth, payment-method shares) and need ×100 + '%'; this is the explicit
// allowlist of the ones that are already a raw count/currency/ratio figure.
const SELLER_PANEL_RAW_NUMBER_METRICS = new Set([
  'GMV',
  'GMV for Shopee (product price after discount, excluded shipping fee)',
  'Number of orders',
  'ROAS',
  'Sum of Ad Cost per Conversion',
]);

export const isSellerPanelPercentageMetric = (metricName: string) =>
  !SELLER_PANEL_RAW_NUMBER_METRICS.has(metricName);

export const formatSellerPanelValue = (metricName: string, value: number) =>
  isSellerPanelPercentageMetric(metricName)
    ? `${roundParseCellValue(value * 100)}%`
    : roundParseCellValue(value);

// Date-range picker support — periods carry starts_at/ends_at from the DB,
// nullable for older/unpopulated rows, so every comparison here tolerates
// missing dates by excluding that period rather than throwing.
export interface PeriodWithDates {
  name: string;
  startsAt?: Date | string | null;
  endsAt?: Date | string | null;
}

const toTime = (value: Date | string | null | undefined): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

// A period's starts_at/ends_at arrive as full timestamps; the date picker
// (@internationalized/date's parseDate) only accepts a plain "YYYY-MM-DD".
export const toIsoDateOnly = (
  value: Date | string | null | undefined
): string | null => {
  const time = toTime(value);
  return time === null ? null : new Date(time).toISOString().slice(0, 10);
};

// Period names whose [startsAt, endsAt] overlaps the picked [start, end]
// range — used by pages that show a true 2-sided data window.
export const resolvePeriodsInRange = (
  periods: PeriodWithDates[],
  range: { start: string; end: string }
): string[] => {
  const rangeStart = toTime(range.start);
  const rangeEnd = toTime(range.end);
  if (rangeStart === null || rangeEnd === null) return [];

  return periods
    .filter((period) => {
      const periodStart = toTime(period.startsAt);
      const periodEnd = toTime(period.endsAt) ?? periodStart;
      if (periodStart === null || periodEnd === null) return false;
      return periodStart <= rangeEnd && periodEnd >= rangeStart;
    })
    .map((period) => period.name);
};

// The single period containing `date`, or the closest one if it falls
// outside every period's range — used by pages that only ever show one
// period at a time, resolved from the range picker's end/max date.
export const resolveNearestPeriod = (
  periods: PeriodWithDates[],
  date: string
): string | undefined => {
  const target = toTime(date);
  if (target === null) return undefined;

  const withDates = periods
    .map((period) => ({
      period,
      start: toTime(period.startsAt),
      end: toTime(period.endsAt),
    }))
    .filter(
      (p): p is { period: PeriodWithDates; start: number; end: number } =>
        p.start !== null && p.end !== null
    );

  if (!withDates.length) return undefined;

  const containing = withDates.find(
    (p) => target >= p.start && target <= p.end
  );
  if (containing) return containing.period.name;

  const distance = (p: (typeof withDates)[number]) =>
    target < p.start ? p.start - target : target - p.end;
  const closest = withDates.reduce((best, current) =>
    distance(current) < distance(best) ? current : best
  );

  return closest.period.name;
};

// Logic util
export const parsePeriod = (period: string) => {
  // Handle quarterly: "Q1 2024"
  const quarterMatch = period.match(/^Q(\d)\s+(\d{4})$/);
  if (quarterMatch) {
    return {
      type: 'quarterly',
      year: Number(quarterMatch[2]),
      quarter: Number(quarterMatch[1]),
      month: null,
    };
  }

  // Handle yearly: "FY 2024" or "FY2024"
  const yearMatch = period.match(/^FY\s?(\d{4})$/);
  if (yearMatch) {
    return {
      type: 'yearly',
      year: Number(yearMatch[1]),
      quarter: null,
      month: null,
    };
  }

  // Handle monthly: "Aug 2025"
  const monthMatch = period.match(/^([A-Za-z]{3,})\s+(\d{4})$/);
  if (monthMatch) {
    const monthName = monthMatch[1].toLowerCase();
    const year = Number(monthMatch[2]);
    const months = [
      'jan',
      'feb',
      'mar',
      'apr',
      'may',
      'jun',
      'jul',
      'aug',
      'sep',
      'oct',
      'nov',
      'dec',
    ];
    const monthIndex = months.indexOf(monthName.slice(0, 3)); // handle both "Aug" or "August"
    return { type: 'monthly', year, month: monthIndex + 1, quarter: null };
  }

  // Fallback for unknown formats
  return { type: 'unknown', year: 0, quarter: 0, month: 0 };
};

export const filterByMetric = (
  dataArray: TradewindsBaseData[] | undefined,
  metric: string
): TradewindsBaseData | undefined => {
  if (!dataArray?.length || !metric) return undefined;

  return dataArray.find((item) => {
    const { metric: m } = item;
    if (!m) return false;
    return m.toLowerCase() === metric.toLowerCase();
  });
};

export const filterByActiveTimeframe = ({
  data,
  activePeriods,
  activeChannels,
  activeCountry,
  activeCategory, // <-- new
}: {
  data?: TradewindsBaseData;
  activePeriods?: string[];
  activeChannels: string[];
  activeCountry: string[];
  activeCategory?: string[]; // <-- new
}) => {
  if (!data || !activePeriods?.length) return [];

  const periodSet = new Set(activePeriods);
  const normalizedChannels = activeChannels.map((c) => c.toLowerCase());
  const normalizedCountries = activeCountry.map((c) => c.toLowerCase());
  const normalizedCategories = (activeCategory || []).map((c) =>
    c.toLowerCase()
  ); // <-- new

  return data.values.filter((d: any) => {
    const channel = d.channel?.name?.toLowerCase() ?? '';
    const country = d.country?.name?.toLowerCase() ?? '';
    const category = d.category?.name?.toLowerCase() ?? ''; // <-- new

    const matchChannel =
      channel === '' ||
      !normalizedChannels.length ||
      normalizedChannels.includes(channel);
    const matchCountry =
      country === '' ||
      !normalizedCountries.length ||
      normalizedCountries.includes(country);
    const matchCategory =
      category === '' ||
      !normalizedCategories.length ||
      normalizedCategories.includes(category); // <-- new
    const matchPeriod = periodSet.has(d.period);

    // delete check monthly if one day it has category
    const periodType = parsePeriod(d.period).type;
    return (
      matchChannel &&
      matchCountry &&
      matchPeriod &&
      (periodType === 'monthly' || matchCategory)
    );
  });
};

export function fillMissingQuarters(periods: string[]): string[] {
  if (periods.length === 0) return [];

  // Convert "Qx YYYY" to a comparable number: YYYY * 4 + (Q - 1)
  const toNumber = (period: string) => {
    const [q, year] = period.split(' ');
    const quarter = Number.parseInt(q.replace('Q', ''));
    return Number.parseInt(year) * 4 + (quarter - 1);
  };

  // Convert number back to "Qx YYYY"
  const toPeriod = (num: number) => {
    const year = Math.floor(num / 4);
    const quarter = (num % 4) + 1;
    return `Q${quarter} ${year}`;
  };

  // Sort periods numerically
  const sorted = [...periods].sort((a, b) => toNumber(a) - toNumber(b));

  const startNum = toNumber(sorted[0]);
  const endNum = toNumber(sorted[sorted.length - 1]);

  const fullRange: string[] = [];
  for (let i = startNum; i <= endNum; i++) {
    fullRange.push(toPeriod(i));
  }

  return fullRange;
}

export const getTotalByPeriod = (data: DataPoint[]) => {
  const grouped: Record<string, number> = {};

  data.forEach((d) => {
    if (!grouped[d.period]) grouped[d.period] = 0;
    grouped[d.period] += d.valueByCategory ?? d.value;
  });

  const parsePeriod = (period: string) => {
    // Normalize whitespace and casing
    const p = period.trim();

    // Quarterly: "Q1 2024"
    const qMatch = p.match(/^Q(\d)\s+(\d{4})$/);
    if (qMatch) {
      const [, q, y] = qMatch.map(Number);
      return { type: 'quarter', year: y, order: q };
    }

    // Yearly: "FY2024" or "FY 2024"
    const fyMatch = p.match(/^FY\s*(\d{4})$/i);
    if (fyMatch) {
      const [, y] = fyMatch.map(Number);
      return { type: 'year', year: y, order: 1 };
    }

    const monthNames = [
      'jan',
      'feb',
      'mar',
      'apr',
      'may',
      'jun',
      'jul',
      'aug',
      'sep',
      'oct',
      'nov',
      'dec',
    ];
    const mMatch = p.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (mMatch) {
      const [, monthStr, yearStr] = mMatch;
      const monthIndex = monthNames.findIndex(
        (m) => m === monthStr.slice(0, 3).toLowerCase()
      );
      return { type: 'month', year: Number(yearStr), order: monthIndex + 1 };
    }

    // Default fallback if unknown format
    return { type: 'unknown', year: 0, order: 0 };
  };

  return Object.entries(grouped)
    .map(([period, total]) => ({ period, Total: total }))
    .sort((a, b) => {
      const pa = parsePeriod(a.period);
      const pb = parsePeriod(b.period);

      // First, sort by year
      if (pa.year !== pb.year) return pa.year - pb.year;

      // Then by order (quarter or month)
      return pa.order - pb.order;
    });
};

export const getMetricAndFiltered = ({
  data,
  metric,
  activePeriods,
  activeChannels,
  activeCountry,
  activeCategory, // <-- new
}: {
  data?: TradewindsBaseData[];
  metric: string;
  activePeriods?: string[];
  activeChannels: string[];
  activeCountry: string[];
  activeCategory?: string[]; // <-- new
}) => {
  const metricData = filterByMetric(data, metric);
  const filtered = filterByActiveTimeframe({
    data: metricData,
    activePeriods,
    activeChannels,
    activeCountry,
    activeCategory, // <-- pass
  });

  return { metricData, filtered };
};

// Module B
export function normalizeAndSortPeriods(
  periods: ModuleBPeriod[]
): ModuleBPeriod[] {
  // Group by period base name (remove " forecast")
  const map = new Map<string, ModuleBPeriod>();

  periods.forEach((p) => {
    const baseName = p.name.replace(' forecast', '').trim();

    if (!map.has(baseName)) {
      map.set(baseName, p);
      return;
    }

    const existing = map.get(baseName)!;

    // Prefer non-forecast
    if (existing.isForecast && !p.isForecast) {
      map.set(baseName, p);
    }
  });

  const cleaned = Array.from(map.values());

  // Sorting: parse "QX YYYY"
  cleaned.sort((a, b) => {
    const [qa, ya] = a.name.replace(' forecast', '').split(' ');
    const [qb, yb] = b.name.replace(' forecast', '').split(' ');
    const quarterA = Number(qa.substring(1));
    const quarterB = Number(qb.substring(1));
    const yearA = Number(ya);
    const yearB = Number(yb);

    return yearA === yearB ? quarterA - quarterB : yearA - yearB;
  });

  return cleaned;
}
export const comparePeriods = (a: string, b: string) => {
  const pa = parsePeriod(stripForecast(a));
  const pb = parsePeriod(stripForecast(b));

  if (pa.year !== pb.year) return pa.year - pb.year;

  if (pa.type === 'quarterly' && pb.type === 'quarterly')
    return (pa.quarter ?? 0) - (pb.quarter ?? 0);
  if (pa.type === 'monthly' && pb.type === 'monthly')
    return (pa.month ?? 0) - (pb.month ?? 0);
  if (pa.type === 'yearly' && pb.type === 'yearly') return 0;

  // fallback: unknown ordering
  return 0;
};
export const stripForecast = (name: string) =>
  name
    .replace(/\bforecast\b/gi, '')
    .replace(/[()]/g, '')
    .trim();

export function normalizeAndSortMetricValues(
  metric: ShopeeForecastMetric
): ShopeeForecastMetric {
  if (!metric?.values?.length) return metric;

  const normalized = metric.values.map((v) => ({
    ...v,
    period: {
      ...v.period,
      name: stripForecast(v.period.name),
    },
  }));

  normalized.sort((a, b) => comparePeriods(a.period.name, b.period.name));

  return { ...metric, values: normalized };
}

interface PrevValues {
  prevQuarterActual: number | null;
  prevYearActual: number | null;
}

export function getPreviousValues(
  values: ShopeeForecastValue[],
  currentIndex: number
): PrevValues {
  if (currentIndex < 0 || !values.length) {
    return { prevQuarterActual: null, prevYearActual: null };
  }

  const current = values[currentIndex];
  const parsedCurrent = parsePeriod(stripForecast(current.period.name));

  let prevQuarterActual: number | null = null;
  let prevYearActual: number | null = null;

  // Traverse backwards to find previous actual values
  for (let i = currentIndex - 1; i >= 0; i--) {
    const v = values[i];
    if (v.period.isForecast) continue;

    const parsed = parsePeriod(stripForecast(v.period.name));

    // previous quarter actual: last actual before current
    if (prevQuarterActual === null) {
      prevQuarterActual = v.value;
    }

    // previous year actual: same quarter previous year
    if (parsedCurrent.quarter != null && parsedCurrent.year != null) {
      if (
        parsed.quarter === parsedCurrent.quarter &&
        parsed.year === parsedCurrent.year - 1
      ) {
        prevYearActual = v.value;
      }
    }

    // Stop early if we have found both
    if (prevQuarterActual !== null && prevYearActual !== null) break;
  }

  return { prevQuarterActual, prevYearActual };
}

const SEA6_COUNTRIES = [
  'Thailand',
  'Indonesia',
  'Singapore',
  'Malaysia',
  'Vietnam',
  'Philippines',
];

const ORDER = ['SHP', 'TTS', 'LZD', 'Meli', 'Coupang', 'MOMO'] as const;

const orderMap: Record<string, number> = ORDER.reduce(
  (acc, key, index) => {
    acc[key] = index;
    return acc;
  },
  {} as Record<string, number>
);

export const sortByPlatformOrder = (arr: string[]) => {
  return [...arr].sort((a, b) => {
    const aIndex = orderMap[a] ?? Number.MAX_SAFE_INTEGER;
    const bIndex = orderMap[b] ?? Number.MAX_SAFE_INTEGER;

    return aIndex - bIndex;
  });
};

export const getCountryDisplay = (
  countries: string[],
  activeFilters: string[],
  isChannels?: boolean
): string => {
  const normalized = countries.map((c) => c.trim());

  // Only keep countries currently in filter
  const filtered =
    activeFilters.length > 0
      ? normalized.filter((c) =>
          activeFilters.some((f) => f.toLowerCase() === c.toLowerCase())
        )
      : normalized; // no filter → show all

  const hasAllSEA6 = SEA6_COUNTRIES.every((c) => filtered.includes(c));

  if (hasAllSEA6) {
    const extras = filtered.filter((c) => !SEA6_COUNTRIES.includes(c));
    return extras.length > 0
      ? `SEA-6, ${extras
          .map((c) => country.data.find((cid) => cid.name === c)?.code || c)
          .join(', ')}`
      : 'SEA-6';
  }

  return filtered
    .map(
      (c) =>
        country.data.find((cid) => cid.name === c)?.code ||
        channels.data.find((cid) => cid.name === c)?.code ||
        c
    )
    .sort((a, b) => {
      if (!isChannels) return 0;
      const aIndex = orderMap[a] ?? Number.MAX_SAFE_INTEGER;
      const bIndex = orderMap[b] ?? Number.MAX_SAFE_INTEGER;
      return aIndex - bIndex;
    })
    .join(', ');
};

// Module C Tab - Metrics map
export const MetricsMap = {
  'revenue metrics': [
    'GMV growth',
    'Order growth',
    'AOV growth',
    // TikTok Shop (channel-specific metric names differ from Shopee's)
    'GMV',
    'Growth in GMV',
    'Order growth rate',
    'AOV growth rate',
    'Number of orders',
  ],
  'take rate and fees metrics': [
    '3P take rate',
    'Commission fee rate',
    'Service fee rate',
    'Transaction fee rate',
    // TikTok Shop
    '3P Take rate',
    'Platform commission fee rate',
    'Order processing fee rate',
    'Affiliate Commission rate',
    'PayLater program fee rate',
  ],
  'subsidies metrics': [
    'Voucher Subsidy in GMV',
    'Shipping subsidy / GMV',
    // TikTok Shop
    'Shipping Cost paid by TTS/GMV',
  ],
  'ad metrics': ['Ad expenses/GMV', 'ROAS'],
  'payment metrics': [
    'Cash on Delivery',
    'Credit / Debit Card',
    'ShopeePay Linked Bank Account',
    'Shopee Wallet',
    'ShopeePay',
    'SPayLater',
    'Apple Pay + Google Pay',
    'Online Payment',
    'Others',
    'QRIS',
    // TikTok Shop
    'Bank Transfer',
    'Credit/Debit card',
    'TikTok Balance',
    'TikTok Pay Later',
    '3rd Party Pay Later',
  ],
  'other metrics': [
    '% GMV from Live',
    'AMS Commission fee rate',
    // TikTok Shop
    '% orders Tokopedia',
    'Tokopedia share in the Gross Revenue',
  ],
};
